import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Alert,
  Box,
  Flex,
  HStack,
  Spinner,
  Text,
  Tooltip,
  chakra,
  useAccordionContext,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import startCase from 'lodash/startCase';
import { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { Else, If, Then } from 'react-if';
import { BGPTable, Path, TextOutput } from '~/components';
import { useConfig } from '~/context';
import { Countdown, DynamicIcon } from '~/elements';
import {
  useColorValue,
  useDevice,
  useFormState,
  useLGQuery,
  useMobile,
  useStrf,
  useTableToString,
} from '~/hooks';
import { isStringOutput, isStructuredOutput } from '~/types';
import { CopyButton } from './copy-button';
import { FormattedError } from './formatted-error';
import { isFetchError, isLGError, isLGOutputOrError, isStackError } from './guards';
import { RequeryButton } from './requery-button';

import type { ErrorLevels } from '~/types';

interface ResultProps {
  index: number;
  queryLocation: string;
}

const AnimatedAccordionItem = motion(AccordionItem);

const getUnderlyingCommand = (queryType: string, target: string): string => {
  const t = target || '8.8.8.8';
  const q = queryType.toLowerCase();
  const isIPv6 = t.includes(':');

  if (q.includes('ping')) {
    return isIPv6
      ? `ping inet6 ${t} count 5`
      : `ping inet ${t} count 5`;
  }
  if (q.includes('traceroute')) {
    return isIPv6
      ? `traceroute inet6 ${t} wait 1`
      : `traceroute inet ${t} wait 1`;
  }
  if (q.includes('bgp_route') || q.includes('bgproute')) {
    return isIPv6
      ? `show route protocol bgp table inet6.0 ${t} detail`
      : `show route protocol bgp table inet.0 ${t} detail`;
  }
  if (q.includes('bgp_community') || q.includes('bgpcommunity')) {
    return isIPv6
      ? `show route protocol bgp table inet6.0 community "${t}" detail`
      : `show route protocol bgp table inet.0 community "${t}" detail`;
  }
  if (q.includes('bgp_aspath') || q.includes('bgpaspath') || q.includes('aspath') || q.includes('as_path')) {
    return isIPv6
      ? `show route protocol bgp table inet6.0 aspath-regex "${t}"`
      : `show route protocol bgp table inet.0 aspath-regex "${t}"`;
  }
  return `show route ${t}`;
};

const AccordionHeaderWrapper = chakra('div', {
  baseStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    _hover: { bg: 'blackAlpha.50' },
    _focus: { boxShadow: 'outline' },
  },
});

const _Result: React.ForwardRefRenderFunction<HTMLDivElement, ResultProps> = (
  props: ResultProps,
  ref,
) => {
  const { index, queryLocation } = props;
  const toast = useToast();
  const { web, cache, messages } = useConfig();
  const { index: indices, setIndex } = useAccordionContext();
  const getDevice = useDevice();
  const device = getDevice(queryLocation);

  const isMobile = useMobile();
  const color = useColorValue('black', 'white');
  const scrollbar = useColorValue('blackAlpha.300', 'whiteAlpha.300');
  const scrollbarHover = useColorValue('blackAlpha.400', 'whiteAlpha.400');
  const scrollbarBg = useColorValue('blackAlpha.50', 'whiteAlpha.50');

  const addResponse = useFormState(s => s.addResponse);
  const form = useFormState(s => s.form);
  const getDirective = useFormState(s => s.getDirective);
  const directive = getDirective();
  const [errorLevel, _setErrorLevel] = useState<ErrorLevels>('error');

  const setErrorLevel = (level: ResponseLevel): void => {
    let e: ErrorLevels = 'error';
    switch (level) {
      case 'success':
        e = level;
        break;
      case 'warning' || 'error':
        e = 'warning';
        break;
    }
    _setErrorLevel(e);
  };

  const { data, error, isLoading, refetch, isFetchedAfterMount } = useLGQuery(
    { queryLocation, queryTarget: form.queryTarget, queryType: form.queryType },
    {
      onSuccess(data) {
        if (device !== null) {
          addResponse(device.id, data);
        }
        if (isLGOutputOrError(data)) {
          console.error(data);
          setErrorLevel(data.level);
        }
      },
      onError(error) {
        console.error({ error });
        if (isLGOutputOrError(error)) {
          setErrorLevel(error.level);
        }
      },
    },
  );

  const isError = useMemo(() => isLGOutputOrError(data), [data, error]);

  const isCached = useMemo(() => data?.cached || !isFetchedAfterMount, [data, isFetchedAfterMount]);

  const strF = useStrf();
  const cacheLabel = strF(web.text.cacheIcon, { time: data?.timestamp });

  const errorKeywords = useMemo(() => {
    let kw = [] as string[];
    if (isLGError(data)) {
      kw = data.keywords;
    }
    return kw;
  }, [data]);

  // Sanitize the raw error message to hide sensitive management network details (IPs, credentials, platform, ports)
  const sanitizeErrorMsg = (message: string): string => {
    const msgLower = message.toLowerCase();
    if (
      msgLower.includes('device settings') ||
      msgLower.includes('connection to device failed') ||
      msgLower.includes('incorrect hostname or ip') ||
      msgLower.includes('wrong tcp port') ||
      msgLower.includes('intermediate firewall blocking access')
    ) {
      const parenIdx = message.indexOf('(');
      if (parenIdx !== -1) {
        return message.substring(0, parenIdx).trim();
      }
      return 'Connection to the network device failed. Please contact the administrator.';
    }
    return message;
  };

  // Map raw error text to a human-friendly message.
  const friendlyErrorMsg = (raw: string): { title: string; detail: string | null } => {
    const s = raw.toLowerCase();
    if (s.includes('no route') || s.includes('unreachable') || s.includes('network unreachable') || s.includes('host unreachable') || s.includes('inacessível') || s.includes('indisponível')) {
      return { title: 'Unreachable Route', detail: 'No route found to the specified target.' };
    }
    if (s.includes('timeout') || s.includes('timed out') || s.includes('expirou')) {
      return { title: 'Request Timeout', detail: 'The remote device did not respond within the configured timeout limit.' };
    }
    if (s.includes('refused') || s.includes('connection refused')) {
      return { title: 'Connection Refused', detail: 'The remote device refused the connection. Please verify device configurations.' };
    }
    if (s.includes('authentication') || s.includes('auth') || s.includes('autenticação') || s.includes('permission denied') || s.includes('login incorrect')) {
      return { title: 'Authentication Failure', detail: 'Invalid credentials configured for the remote device.' };
    }
    if (s.includes('not found') || s.includes('no such') || s.includes('unknown prefix') || s.includes('not in table')) {
      return { title: 'Prefix Not Found', detail: 'The specified prefix or target does not exist in the routing table.' };
    }
    if (s.includes('invalid') || s.includes('bad address') || s.includes('syntax error')) {
      return { title: 'Invalid Input', detail: 'The provided target is invalid for this query type.' };
    }
    if (s.includes('no output') || s.includes('no matching') || s.includes('no results') || s.includes('nenhum resultado')) {
      return { title: 'No Results', detail: 'The query completed successfully, but returned no matching data.' };
    }
    if (
      s.includes('device') ||
      s.includes('connection failed') ||
      s.includes('connect to') ||
      s.includes('failed to connect') ||
      s.includes('error connecting') ||
      s.includes('erro ao conectar')
    ) {
      return { title: 'Device Error', detail: 'Connection to the network device failed. Please contact the administrator.' };
    }
    return { title: raw, detail: null };
  };

  // Parse the the response and/or the error to determine from where to extract the error message.
  const errorMsg = useMemo(() => {
    let msg = messages.general;
    if (isLGError(error)) {
      msg = error.output as string;
    } else if (isLGOutputOrError(data)) {
      msg = data.output as string;
    } else if (isFetchError(error)) {
      msg = startCase(error.statusText);
    } else if (isStackError(error) && error.message.toLowerCase().startsWith('timeout')) {
      msg = messages.requestTimeout;
    } else if (isStackError(error)) {
      msg = startCase(error.message);
    }
    return sanitizeErrorMsg(msg);
  }, [error, data, messages.general, messages.requestTimeout]);

  const tableComponent = useMemo<boolean>(() => {
    let result = false;
    if (data?.format === 'application/json') {
      result = true;
    }
    return result;
  }, [data?.format]);

  let copyValue = data?.output as string;

  const formatData = useTableToString(form.queryTarget, data, [data?.format]);

  if (data?.format === 'application/json') {
    copyValue = formatData();
  }

  if (error) {
    copyValue = errorMsg;
  }

  // Signal to the group that this result is done loading.
  useEffect(() => {
    // Only set the index if it's not already set and the query is finished loading.
    if (Array.isArray(indices) && indices.length === 0 && !isLoading) {
      // Only set the index if the response has data or an error.
      if (data || isError) {
        setIndex([index]);
      }
    }
  }, [data, index, indices, isLoading, isError, setIndex]);

  if (device === null) {
    const id = `toast-queryLocation-${index}-${queryLocation}`;
    if (!toast.isActive(id)) {
      toast({
        id,
        title: messages.general,
        description: `Configuration for device with ID '${queryLocation}' not found.`,
        status: 'error',
        isClosable: true,
      });
    }
    return <></>;
  }

  return (
    <AnimatedAccordionItem
      ref={ref}
      id={device.id}
      isDisabled={isLoading}
      exit={{ opacity: 0, y: 300 }}
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 300 }}
      transition={{ duration: 0.3, delay: index * 0.3 }}
      border="1px solid"
      borderColor="gray.700"
      rounded="lg"
      overflow="hidden"
      boxShadow="xl"
      my={6}
      bg="#0e131f"
    >
      <AccordionButton
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={3}
        px={4}
        bg="#131924"
        _hover={{ bg: "#1b2334" }}
        _focus={{ boxShadow: 'none' }}
        borderBottom="1px solid"
        borderColor="gray.800"
        w="100%"
      >
        <HStack spacing={3} alignItems="center" overflow="hidden" mr={2}>
          <HStack spacing={1.5} display={{ base: 'none', sm: 'flex' }} flexShrink={0}>
            <Box w="3" h="3" rounded="full" bg="#ff5f56" />
            <Box w="3" h="3" rounded="full" bg="#ffbd2e" />
            <Box w="3" h="3" rounded="full" bg="#27c93f" />
          </HStack>
          <Text
            fontFamily="mono"
            fontSize={{ base: '10px', md: 'xs' }}
            fontWeight="bold"
            color="gray.300"
            letterSpacing="wider"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            <chakra.span display={{ base: 'none', md: 'inline' }}>DIAGNOSTIC OUTPUT - </chakra.span>
            {device.name.toUpperCase()}
          </Text>
        </HStack>

        <HStack spacing={4} alignItems="center" flexShrink={0}>
          <Text fontFamily="mono" fontSize={{ base: '10px', md: 'xs' }} color="gray.400" whiteSpace="nowrap">
            {isLoading ? 'Running...' : `Execution: ${(data?.runtime ?? 0).toFixed(3)}s`}
          </Text>
          <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
            {isStructuredOutput(data) && data.level === 'success' && tableComponent && (
              <Path device={device.id} />
            )}
            <CopyButton copyValue={copyValue} isDisabled={isLoading} />
            <RequeryButton requery={refetch} isDisabled={isLoading} />
          </HStack>
        </HStack>
      </AccordionButton>

      <AccordionPanel
        bg="#0e131f"
        p={5}
        overflowX="auto"
        fontFamily="mono"
        color="gray.100"
        css={{
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: '5px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#0e131f',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#1e293b',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#334155',
          },
        }}
      >
        {/* CLI Command Representation */}
        <Box mb={3} color="gray.400" fontSize="sm" lineHeight="tall">
          <Box as="span" color="#38bdf8" mr={2}>$</Box>
          <Box as="span" color="white" fontWeight="semibold">exec </Box>
          <Box as="span" color="gray.500">--router=</Box>
          <Box as="span" color="#38bdf8">"{device.name}" </Box>
          <Box as="span" color="gray.500">--cmd=</Box>
          <Box as="span" color="#38bdf8">"{directive?.name ?? form.queryType}" </Box>
          <Box as="span" color="gray.500">--target=</Box>
          <Box as="span" color="#38bdf8">"{form.queryTarget.join(', ')}"</Box>
        </Box>

        {/* Underlying CLI Router Command */}
        <Box mb={4} color="sky.300" fontSize="sm">
          Command: {getUnderlyingCommand(form.queryType, form.queryTarget.join(', '))}
        </Box>

        {/* Output Area */}
        <Box
          minH="60px"
          maxH="450px"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#0e131f',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#1e293b',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#334155',
            },
          }}
        >
          {isLoading ? (
            <HStack spacing={2} color="gray.400" fontSize="sm" py={4}>
              <Spinner size="xs" color="sky.400" />
              <Text>Executing command on remote router...</Text>
            </HStack>
          ) : (
            <Flex direction="column" flex="1 0 auto" maxW={error ? '100%' : undefined}>
              <If condition={!isError && typeof data !== 'undefined'}>
                <Then>
                  {isStructuredOutput(data) && data.level === 'success' && tableComponent ? (
                    <BGPTable>{data.output}</BGPTable>
                  ) : isStringOutput(data) && data.level === 'success' && !tableComponent ? (
                    <TextOutput bg="transparent" border="none" p={0} mt={0} color="gray.100">{data.output}</TextOutput>
                  ) : (
                    <Box py={2}>
                      {(() => {
                        const rawMsg = sanitizeErrorMsg(isStringOutput(data) ? data.output : errorMsg);
                        const { title, detail } = friendlyErrorMsg(rawMsg);
                        return (
                          <>
                            {title !== rawMsg && (
                              <Box color="red.300" fontSize="sm" fontWeight="semibold">{title}</Box>
                            )}
                            {detail && detail !== rawMsg && (
                              <Box color="gray.400" fontSize="xs" mt={1}>{detail}</Box>
                            )}
                            {(detail === rawMsg || rawMsg !== detail) && (
                              <Box color="red.200" fontSize="xs" mt={1}>
                                <FormattedError message={rawMsg} keywords={errorKeywords} />
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Then>
                <Else>
                  <Box py={2}>
                    {(() => {
                      const { title, detail } = friendlyErrorMsg(errorMsg);
                      return (
                        <>
                          {title !== errorMsg && (
                            <Box color="red.300" fontSize="sm" fontWeight="semibold">{title}</Box>
                          )}
                          {detail && detail !== errorMsg && (
                            <Box color="gray.400" fontSize="xs" mt={1}>{detail}</Box>
                          )}
                          {(detail === errorMsg || errorMsg !== detail) && (
                            <Box color="red.200" fontSize="xs" mt={1}>
                              <FormattedError message={errorMsg} keywords={errorKeywords} />
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                </Else>
              </If>
            </Flex>
          )}
        </Box>

        {/* Status Badge and Blinking Cursor */}
        {!isLoading && (
          <HStack mt={5} spacing={2} alignItems="center">
            <Box
              px={2.5}
              py={1}
              bg={isError ? "red.900" : "emerald.900"}
              color={isError ? "red.200" : "emerald.200"}
              fontSize="xs"
              fontWeight="bold"
              rounded="md"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {isError ? "ERROR" : "SUCCESS"}
            </Box>
            <Box
              as="span"
              fontSize="md"
              fontWeight="bold"
              color={isError ? "red.400" : "emerald.400"}
              css={{
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 0 },
                  '50%': { opacity: 1 },
                },
              }}
            >
              _
            </Box>
          </HStack>
        )}

        {/* Cache countdown if applicable */}
        <Flex direction="row" flexWrap="wrap">
          <HStack
            px={3}
            mt={2}
            spacing={1}
            flex="1 0 auto"
            justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
          >
            <If condition={cache.showText && !isLoading && !isError && isCached}>
              <Then>
                <If condition={isMobile}>
                  <Then>
                    <Countdown timeout={cache.timeout} text={web.text.cachePrefix} color='yellow' />
                    <Tooltip hasArrow label={cacheLabel} placement="top">
                      <Box>
                        <DynamicIcon icon={{ bs: 'BsLightningFill' }} color="yellow" />
                      </Box>
                    </Tooltip>
                  </Then>
                  <Else>
                    <Tooltip hasArrow label={cacheLabel} placement="top">
                      <Box>
                        <DynamicIcon icon={{ bs: 'BsLightningFill' }} color="yellow" />
                      </Box>
                    </Tooltip>
                    <Countdown timeout={cache.timeout} text={web.text.cachePrefix} color='white' />
                  </Else>
                </If>
              </Then>
            </If>
          </HStack>
        </Flex>
      </AccordionPanel>
    </AnimatedAccordionItem>
  );
};

export const Result = memo(forwardRef<HTMLDivElement, ResultProps>(_Result), isEqual);
